// data/cookieClient.ts
import { LanguageType, ThemeType } from "@/lib/types";
import { Language } from "@prisma/client";

export const setLanguageCookie = (language: LanguageType) => {
  if (typeof document !== "undefined") {
    try {
      const cookieString = `language=${language}; path=/; max-age=31536000; SameSite=None; Secure`;
      console.log("Setting language cookie:", cookieString);
      document.cookie = cookieString;
    } catch (error) {
      console.error("Error setting language cookie:", error);
    }
  }
};


export const setThemeCookie = (theme: ThemeType) => {
  if (typeof document !== "undefined") {
    try {
      const cookieString = `theme=${theme}; path=/; max-age=31536000; SameSite=None; Secure`;
      console.log("Setting theme cookie:", cookieString);
      document.cookie = cookieString;
    } catch (error) {
      console.error("Error setting theme cookie:", error);
    }
  }
};

export const getThemeCookie = (): ThemeType => {
  if (typeof document !== "undefined") {
    const cookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("theme="));

    const theme = cookie ? cookie.split("=")[1] : ThemeType.LIGHT; // Default to Light mode
    return theme === "dark" ? ThemeType.DARK : ThemeType.LIGHT; // Ensure only valid values
  }
  return ThemeType.LIGHT; // Default to Light mode
};



export const getLanguageCookie = (): LanguageType => {
  if (typeof document !== "undefined") {
    const cookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("language="));
    
    const language = cookie ? cookie.split("=")[1] : "en-US"; // Default to English
    return language === "ar-SA" ? LanguageType.AR : LanguageType.EN;
  }
  return LanguageType.EN; // Default to English if no cookie is available
};
// // For SSR: Helper function to get language from cookies in headers
// export const getLanguageCookieSSR = (headers: Headers): LanguageType => {
//   const cookie = headers.get("cookie");
//   if (cookie) {
//     const language = cookie.split("; ").find((row) => row.startsWith("language="));
//     const languageValue = language ? language.split("=")[1] : "en-us";
//     return languageValue === "ar-sa" ? LanguageType.AR : LanguageType.EN;
//   }
//   return LanguageType.EN; // Default to English
// };

export const convertToPrismaLanguage = (lang: LanguageType): Language => {
  switch (lang) {
    case LanguageType.EN:
      return Language.en_US;
    case LanguageType.AR:
      return Language.ar_SA;
    default:
      return Language.en_US; // Default to EN if invalid
  }
};
// Convert langParam to a valid LanguageType (fall back to cookie if invalid)
export const getValidLanguage = (
  langParam: string | undefined = getLanguageCookie() // Default to the cookie value if no parameter is passed
): LanguageType => {
  if (langParam) {
    const lang = langParam.toLowerCase();
    if (lang === "en-us" || lang === "en") {
      return LanguageType.EN;
    }
    if (lang === "ar-sa" || lang === "ar") {
      return LanguageType.AR;
    }
  }
  return LanguageType.EN; // Default to English if the language is not recognized
};

// Function to get language and direction
export const getLanguageAndDirection = (
  langParam: string | undefined
): { language: LanguageType; dir: string } => {
  const language = getValidLanguage(langParam); // Convert langParam to valid LanguageType
  const dir = isRtl(language) ? "rtl" : "ltr"; // Determine direction based on language
  return { language, dir };
};

export const getLanguageDirection = (langParam: string | undefined): string => {
  const language = getValidLanguage(langParam); // Convert langParam to valid LanguageType
  const dir = isRtl(language) ? "rtl" : "ltr"; // Determine direction based on language
  return dir;
};

export const isRtl = (
  language: LanguageType = getLanguageCookie()
): boolean => {
  return language === LanguageType.AR; // Arabic language is RTL
};

export const deleteAllCookies = () => {
  const preservedCookies = ["language", "theme"];


  localStorage.removeItem("app-settings");

  document.cookie.split("; ").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!preservedCookies.includes(name)) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
};